'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdManageAccounts,
    MdVideocam,
    MdPlayArrow,
    MdStop,
    MdCancel,
    MdContentCopy,
    MdSend,
    MdEdit,
    MdChat,
    MdPerson,
    MdAccessTime,
    MdCalendarToday,
    MdFiberManualRecord,
    MdPictureAsPdf,
    MdPrint,
    MdTrendingUp,
} from 'react-icons/md'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getTeleconsultation,
    startTeleconsultation,
    endTeleconsultation,
    cancelTeleconsultation,
    resendTeleconsultationLink,
    getRecordingInfo,
    markTeleconsultationBillingAsPaid,
    cancelTeleconsultationBilling,
    TeleconsultationRoom,
    TeleconsultationRecordingInfo,
    TELECONSULTATION_STATUS_LABELS,
    TELECONSULTATION_STATUS_COLORS,
} from '@/lib/api/teleconsultation/teleconsultation-api'
import BillingInfoSection from '@/components/billing/BillingInfoSection'
import { BillingInfoData } from '@/components/billing/BillingInfoSection'
import { printConsultation } from '@/lib/api/consultations/consultation-api'
import ConfirmModal from '@/components/confirm-modal'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function TeleconsultationDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canUpdate, canDelete } = useResourcePermissions('bookings', 'teleconsultations')

    const [teleconsultation, setTeleconsultation] =
        useState<TeleconsultationRoom | null>(null)
    const [recording, setRecording] =
        useState<TeleconsultationRecordingInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [showEndModal, setShowEndModal] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [printing, setPrinting] = useState<boolean>(false)
    const [showSignModal, setShowSignModal] = useState<boolean>(false)

    useEffect(() => {
        if (id) {
            loadTeleconsultation()
        }
    }, [id])

    const loadTeleconsultation = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getTeleconsultation(parseInt(id, 10))
            if (response.success) {
                setTeleconsultation(response.result)

                // Carregar informações de gravação se habilitada
                if (response.result.recordingEnabled) {
                    const recResponse = await getRecordingInfo(
                        parseInt(id, 10)
                    )
                    if (recResponse.success) {
                        setRecording(recResponse.result)
                    }
                }
            } else {
                toast.error(
                    response.error || 'Erro ao carregar teleconsulta'
                )
                navigate('/bookings/teleconsultations')
            }
        } catch {
            toast.error('Erro ao carregar teleconsulta')
            navigate('/bookings/teleconsultations')
        } finally {
            setLoading(false)
        }
    }

    const handleStart = async () => {
        if (!id) return
        setActionLoading(true)
        try {
            const response = await startTeleconsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Teleconsulta iniciada!')
                navigate(`/bookings/teleconsultations/${id}/room`)
            } else {
                toast.error(response.error || 'Erro ao iniciar teleconsulta')
            }
        } catch {
            toast.error('Erro ao iniciar teleconsulta')
        } finally {
            setActionLoading(false)
        }
    }

    const handleEnd = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await endTeleconsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Teleconsulta encerrada')
                loadTeleconsultation()
            } else {
                toast.error(
                    response.error || 'Erro ao encerrar teleconsulta'
                )
            }
        } catch {
            toast.error('Erro ao encerrar teleconsulta')
        } finally {
            setActionLoading(false)
            setShowEndModal(false)
        }
    }

    const handleCancel = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await cancelTeleconsultation(parseInt(id, 10))
            if (response.success) {
                toast.success('Teleconsulta cancelada')
                loadTeleconsultation()
            } else {
                toast.error(
                    response.error || 'Erro ao cancelar teleconsulta'
                )
            }
        } catch {
            toast.error('Erro ao cancelar teleconsulta')
        } finally {
            setActionLoading(false)
            setShowCancelModal(false)
        }
    }

    const handleResendLink = async () => {
        if (!id) return
        try {
            const response = await resendTeleconsultationLink(
                parseInt(id, 10)
            )
            if (response.success) {
                toast.success('Link reenviado com sucesso')
            } else {
                toast.error(response.error || 'Erro ao reenviar link')
            }
        } catch {
            toast.error('Erro ao reenviar link')
        }
    }

    const handleCopyLink = () => {
        if (!teleconsultation) return
        const link =
            teleconsultation.accessLink ||
            `${window.location.origin}/t/${teleconsultation.patientToken}`
        navigator.clipboard.writeText(link)
        toast.success('Link copiado para a área de transferência')
    }

    const handlePrint = async () => {
        const consultationId = teleconsultation?.consultationId
        if (!consultationId) return
        setPrinting(true)
        try {
            await printConsultation(consultationId)
            toast.success('PDF gerado com sucesso!')
        } catch (error) {
            toast.error('Erro ao gerar PDF para impressão')
        } finally {
            setPrinting(false)
        }
    }

    const formatDate = (date: string) => {
        return format(new Date(date), "dd 'de' MMMM 'de' yyyy", {
            locale: ptBR,
        })
    }

    const formatTime = (date: string) => {
        return format(new Date(date), 'HH:mm', { locale: ptBR })
    }

    const formatDuration = (seconds: number) => {
        const minutes = Math.round(seconds / 60)
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60)
            const rem = minutes % 60
            return rem === 0 ? `${hours}h` : `${hours}h ${rem}min`
        }
        return `${minutes} min`
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando teleconsulta...
                </p>
            </div>
        )
    }

    if (!teleconsultation) return null

    const tc = teleconsultation
    const canStart = ['CREATED', 'WAITING_DOCTOR'].includes(tc.status)
    const canEndRoom = ['WAITING_PATIENT', 'IN_PROGRESS'].includes(tc.status)
    const canCancelRoom = [
        'CREATED',
        'WAITING_PATIENT',
        'WAITING_DOCTOR',
    ].includes(tc.status)
    const canEditRoom = tc.status === 'CREATED'

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/bookings/teleconsultations')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <MdVideocam className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">
                                Teleconsulta #{tc.id}
                            </h1>
                            <span
                                className={`badge ${TELECONSULTATION_STATUS_COLORS[tc.status]
                                    }`}
                            >
                                {TELECONSULTATION_STATUS_LABELS[tc.status]}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {tc.consultationId && (
                        <>
                            <button
                                onClick={() => setShowSignModal(true)}
                                className="btn btn-outline btn-secondary btn-sm gap-2"
                                title="Exportar PDF com assinatura digital"
                            >
                                <MdPictureAsPdf className="text-lg" />
                                Exportar PDF
                            </button>
                            <button
                                onClick={handlePrint}
                                disabled={printing}
                                className="btn btn-outline btn-sm gap-2"
                                title="Imprimir com área de assinatura"
                            >
                                {printing ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdPrint className="text-lg" />
                                )}
                                Imprimir
                            </button>
                        </>
                    )}
                    {canEditRoom && canUpdate && (
                        <button
                            onClick={() =>
                                navigate(`/atendimento/scheduling/${tc.id}?type=TELECONSULTATION`)
                            }
                            className="btn btn-ghost btn-sm gap-1"
                        >
                            <MdEdit />
                            Editar
                        </button>
                    )}
                    {canStart && (
                        <button
                            onClick={handleStart}
                            className="btn btn-secondary btn-sm gap-1"
                            disabled={actionLoading}
                        >
                            <MdPlayArrow />
                            Iniciar Sala
                        </button>
                    )}
                    {['WAITING_PATIENT', 'IN_PROGRESS'].includes(tc.status) && (
                        <button
                            onClick={() =>
                                navigate(`/bookings/teleconsultations/${tc.id}/room`)
                            }
                            className="btn btn-secondary btn-sm gap-1"
                        >
                            <MdVideocam />
                            Entrar na Sala
                        </button>
                    )}
                    {canEndRoom && (
                        <button
                            onClick={() => setShowEndModal(true)}
                            className="btn btn-warning btn-sm gap-1"
                            disabled={actionLoading}
                        >
                            <MdStop />
                            Encerrar
                        </button>
                    )}
                    {canCancelRoom && canDelete && (
                        <button
                            onClick={() => setShowCancelModal(true)}
                            className="btn btn-error btn-sm gap-1"
                            disabled={actionLoading}
                        >
                            <MdCancel />
                            Cancelar
                        </button>
                    )}
                </div>
            </div>

            {/* Informações */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dados da teleconsulta */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações
                        </h2>

                        {tc.title && (
                            <div className="mb-3">
                                <p className="text-sm text-base-content/60">
                                    Título
                                </p>
                                <p className="font-medium">{tc.title}</p>
                            </div>
                        )}

                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <MdPerson className="text-base-content/60" />
                                <div>
                                    <p className="text-sm text-base-content/60">
                                        Paciente
                                    </p>
                                    <p className="font-medium">
                                        {tc.client?.name || 'N/A'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <MdPerson className="text-base-content/60" />
                                <div>
                                    <p className="text-sm text-base-content/60">
                                        Profissional
                                    </p>
                                    <p className="font-medium">
                                        {tc.professional.name}
                                    </p>
                                </div>
                            </div>

                            {tc.createdBy && (
                                <div className="flex items-center gap-2">
                                    <MdManageAccounts className="text-base-content/60" />
                                    <div>
                                        <p className="text-sm text-base-content/60">
                                            Agendado por
                                        </p>
                                        <p className="font-medium">
                                            {tc.createdBy.name}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <MdCalendarToday className="text-base-content/60" />
                                <div>
                                    <p className="text-sm text-base-content/60">
                                        Data
                                    </p>
                                    <p className="font-medium">
                                        {formatDate(tc.scheduledAt)}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <MdAccessTime className="text-base-content/60" />
                                <div>
                                    <p className="text-sm text-base-content/60">
                                        Horário
                                    </p>
                                    <p className="font-medium">
                                        {formatTime(tc.scheduledAt)}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <MdAccessTime className="text-base-content/60" />
                                <div>
                                    <p className="text-sm text-base-content/60">
                                        Duração máxima
                                    </p>
                                    <p className="font-medium">
                                        {formatDuration(tc.maxDuration)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Informações Financeiras */}
                        {(tc.price != null || tc.sellingPrice != null || tc.consultation?.price != null) && (
                            <div className="mt-4 pt-4 border-t border-base-300/50">
                                <p className="text-sm font-semibold text-base-content mb-3">Financeiro</p>
                                <div className="grid grid-cols-2 gap-3">
                                    {(tc.price ?? tc.consultation?.price) != null && (tc.price ?? tc.consultation?.price)! > 0 && (
                                        <div>
                                            <p className="text-xs text-base-content/60">Preço de Custo</p>
                                            <p className="font-medium">R$ {((tc.price ?? tc.consultation?.price)! / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                        </div>
                                    )}
                                    {(tc.sellingPrice ?? tc.consultation?.sellingPrice) != null && (tc.sellingPrice ?? tc.consultation?.sellingPrice)! > 0 && (
                                        <div>
                                            <p className="text-xs text-base-content/60">Preço de Venda</p>
                                            <p className="font-medium text-secondary">R$ {((tc.sellingPrice ?? tc.consultation?.sellingPrice)! / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                        </div>
                                    )}
                                    {(tc.markupPercentage ?? tc.consultation?.markupPercentage) != null && (tc.markupPercentage ?? tc.consultation?.markupPercentage)! > 0 && (
                                        <div>
                                            <p className="text-xs text-base-content/60">Porcentagem</p>
                                            <p className="font-medium">{(tc.markupPercentage ?? tc.consultation?.markupPercentage)!.toFixed(1)}%</p>
                                        </div>
                                    )}
                                    {(tc.discount ?? tc.consultation?.discount) != null && (tc.discount ?? tc.consultation?.discount)! > 0 && (
                                        <div>
                                            <p className="text-xs text-base-content/60">Desconto</p>
                                            <p className="font-medium text-accent">{(tc.discount ?? tc.consultation?.discount)}%</p>
                                        </div>
                                    )}
                                    {(tc.finalPrice ?? tc.consultation?.finalPrice) != null && (tc.finalPrice ?? tc.consultation?.finalPrice)! > 0 && (
                                        <div>
                                            <p className="text-xs text-base-content/60">Valor Final</p>
                                            <p className="font-bold text-secondary">R$ {((tc.finalPrice ?? tc.consultation?.finalPrice)! / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {tc.notes && (
                            <div className="mt-4 p-3 bg-base-200/50 rounded-lg">
                                <p className="text-sm text-base-content/60 mb-1">
                                    Observações
                                </p>
                                <p className="text-sm">{tc.notes}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Link de acesso e configurações */}
                <div className="flex flex-col gap-6">
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">
                                Link do Paciente
                            </h2>

                            <div className="bg-base-200/50 rounded-lg p-3 mb-3">
                                <p className="text-sm font-mono break-all">
                                    {tc.accessLink ||
                                        `${window.location.origin}/t/${tc.patientToken}`}
                                </p>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handleCopyLink}
                                    className="btn btn-sm btn-ghost gap-1"
                                >
                                    <MdContentCopy />
                                    Copiar
                                </button>
                                {canCancelRoom && (
                                    <button
                                        onClick={handleResendLink}
                                        className="btn btn-sm btn-ghost gap-1"
                                    >
                                        <MdSend />
                                        Reenviar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">
                                Configurações
                            </h2>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Gravação</span>
                                    <span
                                        className={`badge badge-sm ${tc.recordingEnabled
                                            ? 'badge-success'
                                            : 'badge-ghost'
                                            }`}
                                    >
                                        {tc.recordingEnabled
                                            ? 'Habilitada'
                                            : 'Desabilitada'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Chat</span>
                                    <span
                                        className={`badge badge-sm ${tc.enableChat
                                            ? 'badge-success'
                                            : 'badge-ghost'
                                            }`}
                                    >
                                        {tc.enableChat
                                            ? 'Habilitado'
                                            : 'Desabilitado'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">
                                        Compartilhamento de Tela
                                    </span>
                                    <span
                                        className={`badge badge-sm ${tc.enableScreenShare
                                            ? 'badge-success'
                                            : 'badge-ghost'
                                            }`}
                                    >
                                        {tc.enableScreenShare
                                            ? 'Habilitado'
                                            : 'Desabilitado'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Billing */}
            {tc.billing && (
                <BillingInfoSection
                    billing={tc.billing as BillingInfoData}
                    onMarkAsPaid={async () => {
                        const response = await markTeleconsultationBillingAsPaid(tc.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para marcar como pago foi enviada'
                                )
                            } else {
                                toast.success('Cobrança marcada como paga!')
                            }
                            loadTeleconsultation()
                        } else {
                            toast.error(response.error || 'Erro ao marcar como paga')
                        }
                    }}
                    onCancel={async () => {
                        const response = await cancelTeleconsultationBilling(tc.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para cancelar a cobrança foi enviada'
                                )
                            } else {
                                toast.success('Cobrança cancelada!')
                            }
                            loadTeleconsultation()
                        } else {
                            toast.error(response.error || 'Erro ao cancelar cobrança')
                        }
                    }}
                    canUpdate={canUpdate}
                    onBillingChanged={loadTeleconsultation}
                />
            )}

            {/* Funil de Vendas */}
            {tc.deal && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4 flex items-center gap-2">
                            <MdTrendingUp className="text-secondary" />
                            Funil de Vendas
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm text-base-content/60">Pipeline</span>
                                <p className="font-medium">{tc.deal.pipeline.name}</p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Etapa</span>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: tc.deal.stage.color || '#6b7280' }} />
                                    <span className="font-medium">{tc.deal.stage.name}</span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Oportunidade</span>
                                <p className="font-medium">{tc.deal.title}</p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Status</span>
                                <div>
                                    <span className={`badge badge-sm ${tc.deal.status === 'WON' ? 'badge-success' : tc.deal.status === 'LOST' ? 'badge-error' : tc.deal.status === 'ABANDONED' ? 'badge-warning' : 'badge-info'}`}>
                                        {tc.deal.status === 'OPEN' ? 'Aberta' : tc.deal.status === 'WON' ? 'Ganha' : tc.deal.status === 'LOST' ? 'Perdida' : 'Abandonada'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Lead</span>
                                <p className="font-medium">{tc.deal.lead.name}</p>
                                {tc.deal.lead.phoneNumber && <p className="text-sm text-base-content/60">{tc.deal.lead.phoneNumber}</p>}
                            </div>
                            {tc.deal.totalValue != null && tc.deal.totalValue > 0 && (
                                <div>
                                    <span className="text-sm text-base-content/60">Valor da Oportunidade</span>
                                    <p className="font-medium text-secondary">{(tc.deal.totalValue / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                </div>
                            )}
                        </div>
                        <div className="mt-4">
                            <a href={`/sales/opportunities/${tc.deal.id}`} className="btn btn-sm btn-outline btn-secondary">Ver Oportunidade</a>
                        </div>
                    </div>
                </div>
            )}

            {/* Participantes */}
            {tc.participants && tc.participants.length > 0 && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Participantes
                        </h2>

                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Papel</th>
                                        <th>Entrada</th>
                                        <th>Saída</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tc.participants.map(p => (
                                        <tr key={p.id}>
                                            <td>{p.name}</td>
                                            <td>
                                                <span className="badge badge-sm badge-ghost">
                                                    {p.role === 'PROFESSIONAL'
                                                        ? 'Profissional'
                                                        : 'Paciente'}
                                                </span>
                                            </td>
                                            <td>
                                                {format(
                                                    new Date(p.joinedAt),
                                                    'HH:mm',
                                                    { locale: ptBR }
                                                )}
                                            </td>
                                            <td>
                                                {p.leftAt
                                                    ? format(
                                                        new Date(p.leftAt),
                                                        'HH:mm',
                                                        { locale: ptBR }
                                                    )
                                                    : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Gravação */}
            {recording && recording.recordingEnabled && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            <MdFiberManualRecord className="text-error" />
                            Gravação
                        </h2>

                        {recording.hasRecording ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    {recording.recordingDuration && (
                                        <div>
                                            <p className="text-sm text-base-content/60">
                                                Duração
                                            </p>
                                            <p className="font-medium">
                                                {formatDuration(
                                                    recording.recordingDuration
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {recording.recordingSize && (
                                        <div>
                                            <p className="text-sm text-base-content/60">
                                                Tamanho
                                            </p>
                                            <p className="font-medium">
                                                {(
                                                    recording.recordingSize /
                                                    1024 /
                                                    1024
                                                ).toFixed(1)}{' '}
                                                MB
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {recording.recordingUrl && (
                                    <div className="bg-base-200/50 rounded-lg p-4">
                                        <video
                                            controls
                                            className="w-full max-h-96 rounded-lg"
                                            src={`${window.location.origin
                                                }/${recording.recordingUrl}`}
                                        >
                                            Seu navegador não suporta
                                            reprodução de vídeo.
                                        </video>
                                    </div>
                                )}

                                {recording.consultationId && (
                                    <p className="text-sm text-base-content/60">
                                        Consulta vinculada: #{recording.consultationId}
                                        {' - '}
                                        A transcrição pode ser iniciada pela
                                        página da consulta.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-base-content/50">
                                <MdFiberManualRecord className="text-3xl mx-auto mb-2 opacity-30" />
                                <p className="text-sm">
                                    {tc.status === 'COMPLETED'
                                        ? 'Gravação em processamento...'
                                        : 'A gravação estará disponível após o encerramento da teleconsulta.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Chat */}
            {tc.chatMessages && tc.chatMessages.length > 0 && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            <MdChat className="text-base-content/60" />
                            Histórico do Chat
                        </h2>

                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {tc.chatMessages.map(msg => (
                                <div
                                    key={msg.id}
                                    className={`flex flex-col ${msg.senderRole === 'PROFESSIONAL'
                                        ? 'items-end'
                                        : 'items-start'
                                        }`}
                                >
                                    <div
                                        className={`max-w-[80%] rounded-lg p-3 ${msg.senderRole === 'PROFESSIONAL'
                                            ? 'bg-secondary/10'
                                            : 'bg-base-200'
                                            }`}
                                    >
                                        <p className="text-xs font-semibold mb-1">
                                            {msg.senderName}
                                        </p>
                                        <p className="text-sm">
                                            {msg.message}
                                        </p>
                                        <p className="text-xs text-base-content/50 mt-1">
                                            {format(
                                                new Date(msg.createdAt),
                                                'HH:mm',
                                                { locale: ptBR }
                                            )}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {tc.consultationId && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    documentType="CONSULTATION"
                    documentId={tc.consultationId}
                    documentTitle={`Teleconsulta #${tc.id} - ${tc.client?.name || ''}`}
                    onSigned={() => setShowSignModal(false)}
                />
            )}

            <ConfirmModal
                isOpen={showEndModal}
                onClose={() => setShowEndModal(false)}
                onConfirm={handleEnd}
                title="Encerrar teleconsulta"
                message="Deseja realmente encerrar esta teleconsulta? A chamada será finalizada para todos os participantes."
                loading={actionLoading}
                confirmButtonText="Encerrar"
            />

            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancel}
                title="Cancelar teleconsulta"
                message="Deseja realmente cancelar esta teleconsulta? Esta ação não pode ser desfeita."
                loading={actionLoading}
                confirmButtonText="Cancelar"
            />
        </div>
    )
}
